import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Download,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle,
  ErrorOutline,
  AccessTime,
  CloudUpload,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import authService from '../shared/services/authService';
import FileViewerModal from './FileViewerModal';

interface UserFile {
  id: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  storage_type: string;
  is_processed: boolean;
  is_deleted: boolean;
  created_at: string;
  file_exists: boolean;
  view_url: string;
  download_url: string;
}

const UserFilesList: React.FC = () => {
  const theme = useTheme();
  const [files, setFiles] = useState<UserFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UserFile | null>(null);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authService.getUserFiles({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });

      setFiles(response.files || []);
      setTotal(response.pagination?.total || 0);

    } catch (error: any) {
      console.error('Failed to load user files:', error);
      setError('Failed to load files');
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (mimeType === 'application/pdf') {
      return <PdfIcon color="error" />;
    } else {
      return <FileIcon color="action" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleViewFile = (file: UserFile) => {
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFile(null);
  };

  const handleDownloadFile = async (file: UserFile) => {
    try {
      const link = document.createElement('a');
      link.href = file.download_url;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  if (loading && files.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CloudUpload sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          My Files
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {files.length === 0 && !loading ? (
        <Alert severity="info">
          No files found. Upload some documents to see them here.
        </Alert>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Storage</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(file.mime_type)}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {file.original_filename}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {file.id.slice(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(file.file_size)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {file.mime_type}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={file.storage_type.toUpperCase()}
                        size="small"
                        color={file.storage_type === 's3' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {file.is_processed ? (
                          <Chip
                            icon={<CheckCircle />}
                            label="Processed"
                            color="success"
                            size="small"
                            variant="filled"
                          />
                        ) : (
                          <Chip
                            icon={<AccessTime />}
                            label="Pending"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {!file.file_exists && (
                          <Chip
                            icon={<ErrorOutline />}
                            label="Missing"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(file.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {file.file_exists && (
                          <Tooltip title="View File">
                            <IconButton
                              size="small"
                              onClick={() => handleViewFile(file)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        )}
                        {file.file_exists && (
                          <Tooltip title="Download File">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(file)}
                              color="secondary"
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* File Viewer Modal */}
      <FileViewerModal
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        transactionId={selectedFile?.id || ''}
        filename={selectedFile?.original_filename}
        isAdmin={false}
      />
    </Paper>
  );
};

export default UserFilesList;